import sample from './sample.json'
import {
  materialRenderers,
  materialCells,
} from '@jsonforms/material-renderers';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { JsonForms } from '@jsonforms/react';
import {
  createAjv,
  isVisible,
  Generate,
} from '@jsonforms/core';
import {
  Button,
  Step,
  StepButton,
  Stepper,
  Typography,
  Box,
  LinearProgress,
  MobileStepper,
  useMediaQuery,
  Modal,
  TextField,
  IconButton,
  InputLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import { makeStyles } from '@mui/styles';
import {
  getForm,
} from '../actions/Form';
import {
  getResponse,
  saveResponse,
  saveResponseFile,
  saveResponseSignedFile,
  downloadResponse,
} from '../actions/Response';
import useDocumentTitle from './useDocumentTitle';
import { TypingValidateMobile } from '../util/General';
import ReCAPTCHA from "react-google-recaptcha";

// Custom Renderers
import TextControl from './CustomRenderers/Text/TextControl';
import TextControlTester from './CustomRenderers/Text/TextControlTester';
import ImageControl from './CustomRenderers/Image/ImageControl';
import ImageControlTester from './CustomRenderers/Image/ImageControlTester';
import FileControl from './CustomRenderers/File/FileControl';
import FileControlTester from './CustomRenderers/File/FileControlTester';
import MultipleChoiceGridControl from './CustomRenderers/MultipleChoiceGrid/MultipleChoiceGridControl';
import MultipleChoiceGridControlTester from './CustomRenderers/MultipleChoiceGrid/MultipleChoiceGridControlTester';
import CheckboxesGridControl from './CustomRenderers/CheckboxesGrid/CheckboxesGridControl';
import CheckboxesGridControlTester from './CustomRenderers/CheckboxesGrid/CheckboxesGridControlTester';
import CheckboxesControl from './CustomRenderers/Checkboxes/CheckboxesControl';
import CheckboxesControlTester from './CustomRenderers/Checkboxes/CheckboxesControlTester';
import CustomHtmlControl from './CustomRenderers/CustomHtml/CustomHtmlControl';
import CustomHtmlControlTester from './CustomRenderers/CustomHtml/CustomHtmlControlTester';
import ContactNumberControl from './CustomRenderers/ContactNumber/ContactNumberControl';
import ContactNumberControlTester from './CustomRenderers/ContactNumber/ContactNumberControlTester';
import EmailControl from './CustomRenderers/Email/EmailControl';
import EmailControlTester from './CustomRenderers/Email/EmailControlTester';
import MoneyControl from './CustomRenderers/Money/MoneyControl';
import MoneyControlTester from './CustomRenderers/Money/MoneyControlTester';
import MoneyOnlyControl from './CustomRenderers/MoneyOnly/MoneyOnlyControl';
import MoneyOnlyControlTester from './CustomRenderers/MoneyOnly/MoneyOnlyControlTester';
import DateControl from './CustomRenderers/Date/DateControl';
import DateControlTester from './CustomRenderers/Date/DateControlTester';
import TimeControl from './CustomRenderers/Time/TimeControl';
import TimeControlTester from './CustomRenderers/Time/TimeControlTester';

const handleDefaultsAjv = createAjv({useDefaults: true});

const useStyles = makeStyles({
  appform: {
    margin: 'auto',
    padding: '1rem',
    width: '100%',
  },
});

const buttonWrapperStyle = {
  textAlign: 'right',
  width: '100%',
  margin: '1em auto',
};
const buttonSubmitStyle = {
  float: 'right',
  marginLeft: '1em',
};
const buttonNextStyle = {
  float: 'right',
};
const buttonSaveStyle = {
  float: 'right',
  marginLeft: '1em',
};
const buttonStyle = {
  marginRight: '1em',
};

const allRenderers = [
  ...materialRenderers,
  { renderer: TextControl, tester: TextControlTester },
  { renderer: ImageControl, tester: ImageControlTester },
  { renderer: FileControl, tester: FileControlTester },
  { renderer: MultipleChoiceGridControl, tester: MultipleChoiceGridControlTester },
  { renderer: CheckboxesGridControl, tester: CheckboxesGridControlTester },
  { renderer: CheckboxesControl, tester: CheckboxesControlTester },
  { renderer: CustomHtmlControl, tester: CustomHtmlControlTester },
  { renderer: ContactNumberControl, tester: ContactNumberControlTester },
  { renderer: EmailControl, tester: EmailControlTester },
  { renderer: MoneyControl, tester: MoneyControlTester },
  // { renderer: MoneyOnlyControl, tester: MoneyOnlyControlTester },
  { renderer: DateControl, tester: DateControlTester },
  { renderer: TimeControl, tester: TimeControlTester },
]

// Allowed file types for signed documents
const allowedFileExtensions = [
  "image/png", ".png",
  "image/jpeg", ".jpg", ".jpeg",
  "image/gif", ".gif",
  "application/pdf", ".pdf",
  "application/msword", ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ".docx",
]

const maxSignedFileSizeInMB = 20
const maxSignedFileSize = maxSignedFileSizeInMB * 1024 * 1024; // 10MB in bytes

const Form = ({ formId, sessionId, sessionIdFromParam }) => {
  const recaptchaRef = React.useRef();
  // const location = useLocation();
  // const searchParams = new URLSearchParams(location.search);
  // const formId = searchParams?.get('id');
  const dispatch = useDispatch();
  const theme = useTheme();
  const isButtonColor = !!theme.palette.myButtonColor;
  // console.log('theme.palette.myButtonColor', theme.palette.myButtonColor)
  // console.log('theme.palette.myButtonTextColor', theme.palette.myButtonTextColor)

  const { formData, loading } = useSelector((state) => state.form);
  const { responseData, fileUploadResult } = useSelector((state) => state.response);
  const classes = useStyles();
  const [data, setData] = useState(responseData ?? {});
  const [initial_data, setInitialData] = useState(responseData ?? {});
  const [errors, setErrors] = useState([]);
  const [visibleErrors, setVisibleErrors] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  // const [categories, setCategories] = useState();
  const [currentSchema, setCurrentSchema] = useState(null);
  const [isFormCompleted, setIsFormCompleted] = useState(false);
  const [additionalErrors, setAdditionalErrors] = useState([]);
  const [fileUploadList, setFileUploadList] = useState([]);
  const [saveAndCloseModal, setSaveAndCloseModal] = useState(false);
  const [uploadSignedForm, setUploadSignedForm] = useState();
  const [signedFileSizeError, setSignedFileSizeError] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  // Handle reCAPTCHA success
  const handleCaptchaChange = (token) => {
    console.log('token>>', token)
    setCaptchaToken(token);
  };

  const categories = useMemo(() =>
    formData?.uischema?.elements?.filter(elem => {
      return isVisible(elem, data, undefined, handleDefaultsAjv)
    }).map(elem => {
      // let newElem = {...elem, type: 'VerticalLayout'}
      elem.type = 'VerticalLayout'
      // delete elem.rule
      return elem
    }) ?? {}
  , [formData, data])

  const invalidData = useMemo(() => {
    // const found = data && Object.values(data).find(el => el?.is_valid === false)
    // return found ? true : false
    return false
  }, [formData, data])
  // console.log('invalidData', invalidData)

  useDocumentTitle(formData?.form_title ?? 'Online Application')

  // effect to update favicon
  useEffect(() => {
    if (formData?.theme?.favicon && formData?.theme?.favicon.startsWith('https')) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = formData?.theme?.favicon;
    }
  }, [formData?.theme?.favicon]);

  useEffect(() => {
    setCurrentSchema(getSchemaOfCategory(activeCategory))
  }, [activeCategory])

  useEffect(() => {
    if (errors?.length > 0) {
      let newErrors = []
      for (let i = 0; i < errors.length; i++) {
        let currentError = errors[i]
        if (currentError.schemaPath === '#/required' && currentError.params.missingProperty) {
          const { missingProperty } = currentError.params
          let property = currentSchema?.properties?.[missingProperty]
          if (property) {
            const visible = isVisible(property, data, undefined, handleDefaultsAjv)
            // console.log('isVisible', missingProperty, visible)
            if (!visible) {
              continue
            }
          }
        }
        newErrors.push(currentError)
      }
      // console.log(newErrors)
      setVisibleErrors(newErrors)
    } else {
      setVisibleErrors([])
    }
  }, [errors, data])

  useEffect(() => {
    if (formData?.schema &&
        formData?.uischema &&
        formData?.uischema?.type === 'Categorization' &&
        formData?.uischema?.elements?.length > 0)
    {
      const schemaKeys = formData?.schema?.properties ? Object.keys(formData?.schema?.properties) : []
      let allFileUploads = {}
      if (schemaKeys.length) {
        for (let key of schemaKeys) {
          if (formData?.schema?.properties[key].upload) {
            allFileUploads[key] = formData?.schema?.properties[key]
          }
        }
        if (Object.keys(allFileUploads)?.length > 0) {
          setFileUploadList(allFileUploads)
        }
      }
      if (activeCategory === null) {
        if (formData?.signature === 'written' &&
            data?.status === 'incomplete' &&
            data?.session_current_page === data?.session_total_pages - 1 &&
            data?.session_total_pages - 1 >= 0
        ) {
          setActiveCategory(data?.session_total_pages - 1)
        } else {
          setActiveCategory(0)
        }
      }
    }
  }, [formData, data]);

  useEffect(() => {
    // console.log('fileUploadResult', fileUploadResult)
    if (fileUploadResult?.error) {
      const newError = {
          // AJV style path to the property in the schema
          instancePath: fileUploadResult?.instance_path,
          // message to display
          message: fileUploadResult?.error_message,
          schemaPath: `#/properties${fileUploadResult?.instance_path}`,
          keyword: '',
          params: {},
      };
      const additionalErrorsWithoutInstance = additionalErrors.filter(el => el.instancePath !== fileUploadResult?.instance_path);
      setAdditionalErrors([...additionalErrorsWithoutInstance, newError]);
    }
  }, [fileUploadResult])

  const handleChange = (changes) => {
    setData({...data, ...changes.data})
    setErrors(changes.errors)

    // console.log('data', changes.data)
    // console.log('errors', changes.errors)
    // console.log('fileUploadList', fileUploadList)

    let client_id = formId.split(":")[0]
    let form_id = formId.split(":")[1]
    let fileUploadKeys = Object.keys(fileUploadList)
    if (fileUploadKeys?.length > 0) {
      const responseId = `${form_id}:${sessionId ?? sessionIdFromParam}`;
      for (let key of fileUploadKeys) {
        if (data[key] && data[key].name) {
          require('formdata-polyfill')
          const upload_data = new FormData()
          upload_data.append(
            'file',
            data[key],
            data[key].name
          )
          upload_data.append('id', responseId)
          upload_data.append('client_id', client_id)
          upload_data.append('key', key)
          upload_data.append('data', JSON.stringify({ client_id, key }))
          dispatch(saveResponseFile({ id: responseId, data: upload_data }))
        }
      }
    }
  }

  const handleStep = async (step) => {
    if (!formData?.autosave?.enable && activeCategory === 0) {
      await recaptchaRef.current.executeAsync();
    }
    // console.log('handleStep', step)
    let newValue = {...formData?.initial_data, ...data};
    setInitialData(newValue);
    setActiveCategory(step);
    setErrors([]);
    saveCurrentResponse(false);
  }

  const handleSubmit = async () => {
    if (!formData?.autosave?.enable && activeCategory === 0) {
      await recaptchaRef.current.executeAsync();
    }
    saveCurrentResponse(true);
    setIsFormCompleted(true);
    if (formData?.settings?.redirect_link) {
      setTimeout(() => {
        window.open(formData?.settings?.redirect_link, '_self');
      }, 2000)
    }
  }

  const handleSaveAndClose = () => {
    saveCurrentResponse(false);
    window.opener = null;
    if (formData?.settings?.redirect_save_link) {
      setTimeout(() => {
        window.open(formData?.settings?.redirect_save_link, '_self');
      }, 1000)
    } else {
      window.open("about:blank", "_self")
      window.close();
    }
  }

  const saveCurrentResponse = (finalPage) => {
    let currentStep = activeCategory + 1
    let new_response = { ...data }
    new_response.client_id = formId.split(":")[0]
    new_response.form_id = formId.split(":")[1]
    new_response.session_id = new_response.session_id ?? sessionId
    new_response.final_page = finalPage
    new_response.status = finalPage ? 'complete' : 'incomplete'
    new_response.session_current_page = currentStep
    new_response.session_total_pages = categories?.length ?? 0
    // console.log('new_response', new_response)

    if (finalPage) {
      new_response.signed_documents = uploadSignedForm ? true : false
      saveSignedForm(new_response)
    }

    dispatch(saveResponse(new_response))
  }

  const saveSignedForm = (response) => {
    if (uploadSignedForm && response) {
      require('formdata-polyfill')
      const data = new FormData()
      data.append('file', uploadSignedForm, uploadSignedForm?.name)
      data.append('id', response.session_id)
      data.append('client_id', response.client_id)
      data.append('key', 'signed_form')
      data.append('session_id', response.session_id)
      data.append('data', JSON.stringify(response))

      dispatch(saveResponseSignedFile(data))
    }
  }

  const getSchemaOfCategory = (index) => {
    // console.log('categories', categories?.[index])
    let category = categories?.[index]
    let properties = formData?.schema?.properties ?? {}
    let schema = {
      type: "object",
      properties: {}
    }
    category?.elements?.map(el => {
      let propertyId = el.scope.replace("#/properties/", "");
      schema.properties[propertyId] = properties[propertyId]
      // Note: property is a custom html so add the custom html in the schema
      if (properties[propertyId]?.format === 'custom_html' && properties[propertyId]?.template) {
        properties[propertyId].html = formData?.custom_html?.[propertyId] ?? ''
      }
    })
    let required = Object.values(schema?.properties)?.filter(el => el.is_required)?.map(el => el.property_id)
    schema.required = required ?? []

    // Check default data
    if (Object.keys(initial_data).length === 0) {
      let defaultData = {}
      for (let key in properties) {
        // Note: Needs to set initial data for array because rule for contains does not render properly when this field is undefined.
        if (properties[key].type === "array" && properties[key].items?.enum?.length > 0) {
          defaultData[key] = []
        }
      }
      // console.log('revert', defaultData)
      setInitialData(defaultData)
    } else {
      // console.log('initial_data', initial_data)
      let checkboxProperties = Object.values(properties)?.filter(el => el.type === 'array' && el.items?.enum?.length > 0)
      let newData = { ...initial_data }
      let changed = false
      for (let prop of checkboxProperties) {
        if (!newData[prop.property_id]) {
          newData[prop.property_id] = []
          changed = true
        }
      }
      if (changed) {
        // console.log('newData', newData)
        setInitialData(newData)
      }
    }

    // console.log('schema', schema)
    return schema
  }

  const matches = useMediaQuery('(min-width:600px)');

  // console.log('1 schema', currentSchema)
  // console.log('2 categories', categories?.[activeCategory])
  // Note: Do not display if schema and uischema does not match to avoid "No applicable renderer found"
  const isFormReady = Object.keys(currentSchema?.properties ?? {})?.length > 0 &&
    categories?.length > 0 &&
    categories[activeCategory] &&
    categories[activeCategory].elements?.length > 0 &&
    categories[activeCategory].elements[0].scope &&
    currentSchema?.properties[categories[activeCategory].elements[0].scope.replace('#/properties/', '')]?.type

  const getCategories = (data) => {
    const categories = formData?.uischema?.elements
      ?.filter(elem => {
        return isVisible(elem, data, undefined, handleDefaultsAjv)
      })
      ?.map(elem => {
        elem.type = 'VerticalLayout'
        elem.elements = elem.elements?.filter(field => {
          return isVisible(field, data, undefined, handleDefaultsAjv)
        })
        return elem
      })
    return categories
  }

  const isSignaturePage = formData.signature === 'written' && categories?.length - 1 === activeCategory

  const handleDownload = () => {
    let currentStep = activeCategory + 1
    let new_response = { ...data }
    new_response.client_id = formId.split(":")[0]
    new_response.form_id = formId.split(":")[1]
    new_response.session_id = new_response.session_id ?? sessionId
    new_response.session_current_page = currentStep
    new_response._id = new_response._id ? new_response._id : uuidv4()

    let categories = {};
    categories[new_response._id] = getCategories(new_response);

    const params = {
      form: formData,
      responses: [new_response],
      categories: categories,
    }
    dispatch(downloadResponse(JSON.stringify(params)))
  }

  const handleUpload = (event) => {
    // console.log('uploadSignedForm>>', event.target.files[0])
    const file = event.target.files[0];

    if (file && file.size > maxSignedFileSize) {
      setSignedFileSizeError(true)
      setUploadSignedForm(null)
    } else {
      setSignedFileSizeError(false)
      setUploadSignedForm(file)
    }
  }

  return (
    <div className="Form">
      <div className={classes.appform}>
        { isFormCompleted ?
          formData?.settings?.redirect_link ?
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
          :
          <Typography variant={'h6'} gutterBottom>Thank you for completing the form!</Typography>
          :
          formData?.schema && formData?.uischema && formData?.initial_data ?
          <>
            {categories?.length > 1 && matches &&
              <Stepper activeStep={activeCategory}>
                {categories.map((category, idx) => (
                  <Step key={category.label + idx}>
                    <StepButton onClick={() => handleStep(idx)}>
                      {/*category.label + ` idx=${idx}`*/}
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
            }
            {isFormReady ?
              <JsonForms
                schema={currentSchema}
                uischema={
                  categories?.length > 0 ?
                  categories[activeCategory]
                  :
                  {}
                }
                data={initial_data}
                renderers={allRenderers}
                cells={materialCells}
                onChange={handleChange}
                ajv={handleDefaultsAjv}
                additionalErrors={additionalErrors}
              />
              :
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            }
            {!formData?.autosave?.enable && activeCategory === 0 &&
              <ReCAPTCHA
                ref={recaptchaRef}
                onChange={handleCaptchaChange}
                size="invisible"
                sitekey="6LeoSdwUAAAAALNViRy28XIhoEL37uOM70wEcL-m"
              />
            }
            {isSignaturePage &&
              <div style={{maxWidth: '500px', margin: '0 auto', padding: '10px'}}>
                <Button
                  color='primary'
                  variant='contained'
                  onClick={handleDownload}
                >
                  Download PDF
                </Button>
                <Typography variant={'h6'} gutterBottom>
                  PLEASE DOWNLOAD YOUR APPLICATION, SIGN AND UPLOAD
                </Typography>
                <InputLabel style={{ marginTop: '0.2em' }}>Upload File</InputLabel>
                <TextField
                  variant="standard"
                  type="text"
                  value={uploadSignedForm?.name ?? ''}
                  fullWidth={true}
                  InputProps={{
                    endAdornment: (
                      <IconButton component="label">
                        <FileUploadOutlined />
                        <input
                          styles={{display:"none"}}
                          type="file"
                          hidden
                          onChange={handleUpload}
                          name={'signed_form'}
                          accept={allowedFileExtensions.join()}
                        />
                      </IconButton>
                    ),
                    readonly: true
                  }}
                  style={{ marginBottom: !uploadSignedForm ? '0px' : undefined }}
                  error={!uploadSignedForm ? true : false}
                  helperText={signedFileSizeError ? `File size exceeds ${maxSignedFileSizeInMB}MB` : undefined}
                />
              </div>
            }
            <div style={buttonWrapperStyle}>
              {categories?.length - 1 === activeCategory && matches &&
                <Button
                  style={categories?.length > 1 ? buttonNextStyle : {}}
                  color={isButtonColor ? 'myButtonColor' : 'primary'}
                  sx={{ color: theme?.palette?.myButtonTextColor?.main ?? undefined }}
                  variant='contained'
                  onClick={handleSubmit}
                  disabled={visibleErrors?.length > 0 || invalidData || (isSignaturePage && !uploadSignedForm)}
                >
                  Submit
                </Button>
              }
              {/*categories?.length > 1 && categories?.length - 1 != activeCategory && matches &&
                <Button
                  style={buttonSaveStyle}
                  color='success'
                  variant='contained'
                  onClick={()=>setSaveAndCloseModal(true)}
                >
                  Save & Close
                </Button>
              */}
              {(categories?.length > 0 && matches && categories?.length - 1 !== activeCategory) &&
                <Button
                  style={buttonNextStyle}
                  variant='contained'
                  color={isButtonColor ? 'myButtonColor' : 'primary'}
                  sx={{ color: theme?.palette?.myButtonTextColor?.main ?? undefined }}
                  disabled={visibleErrors?.length > 0 || invalidData}
                  onClick={() => handleStep(activeCategory + 1)}
                >
                  Next
                </Button>
              }
              {categories?.length > 1 && matches &&
                <Button
                  style={buttonStyle}
                  color={isButtonColor ? 'myButtonColor' : 'secondary'}
                  sx={{ color: theme?.palette?.myButtonTextColor?.main ?? undefined }}
                  variant='contained'
                  disabled={activeCategory <= 0}
                  onClick={() => handleStep(!isSignaturePage ? activeCategory - 1 : 0)}
                >
                  {!isSignaturePage ? 'Previous' : 'Edit Responses'}
                </Button>
              }
              {categories?.length > 0 && !matches &&
                <MobileStepper
                  variant="text"
                  steps={categories.length}
                  position="static"
                  activeStep={activeCategory}
                  nextButton={
                    categories?.length - 1 === activeCategory ?
                    <Button
                      size="small"
                      color={isButtonColor ? 'myButtonColor' : 'primary'}
                      onClick={handleSubmit}
                      disabled={visibleErrors?.length > 0 || invalidData}
                    >
                      Submit
                      <KeyboardArrowRight />
                    </Button>
                    :
                    <Button
                      size="small"
                      color={isButtonColor ? 'myButtonColor' : 'primary'}
                      onClick={() => handleStep(activeCategory + 1)}
                      disabled={visibleErrors?.length > 0 || invalidData}
                    >
                      Next
                      <KeyboardArrowRight />
                    </Button>
                  }
                  backButton={
                    categories?.length > 1 &&
                    <Button
                      size="small"
                      color={isButtonColor ? 'myButtonColor' : 'primary'}
                      onClick={() => handleStep(!isSignaturePage ? activeCategory - 1 : 0)}
                      disabled={activeCategory <= 0}
                    >
                      <KeyboardArrowLeft />
                      {!isSignaturePage ? 'Back' : 'Edit'}
                    </Button>
                  }
                />
              }
            </div>
          </>
          :
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        }
      </div>
      {saveAndCloseModal &&
        <SaveAndCloseModal
          saveAndCloseModal={saveAndCloseModal}
          setSaveAndCloseModal={setSaveAndCloseModal}
          handleSubmit={handleSaveAndClose}
          visibleErrors={visibleErrors}
          invalidData={invalidData}
        />
      }
    </div>
  );
}

const SaveAndCloseModal = ({
  saveAndCloseModal,
  setSaveAndCloseModal,
  handleSubmit,
  visibleErrors,
  invalidData,
}) => {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };
  return (
    <Modal
      open={saveAndCloseModal}
      onClose={()=>setSaveAndCloseModal(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography id="modal-modal-title" variant="h6" component="h2">
          Save & Close
        </Typography>
        <Typography id="modal-modal-description" sx={{ mt: 2 }}>
          Are you sure you want to save and close the form?
        </Typography>
        <Button
          style={buttonStyle}
          color='primary'
          variant='contained'
          onClick={handleSubmit}
        >
          Save & Close
        </Button>
        <Button
          style={buttonStyle}
          color='secondary'
          variant='contained'
          onClick={()=>setSaveAndCloseModal(false)}
        >
          Cancel
        </Button>
      </Box>
    </Modal>
  )
}

export default Form;
